import { useCallback, useMemo, type ComponentType } from "react";

import {
  ContextMenu,
  Icon,
  MenuItem,
  MenuItemLabel,
  PropertiesPanelSection,
  Tooltip,
} from "@skilitics/designer-ui-components";
import { useDynamicFormatString, useFormatMessage, type FormatMessages } from "@skilitics/localization";
import type { TriggerConditionBuild, TriggerDetails } from "@skilitics/nautilus-core-designer-data";

import { designerNodeOrNullSelector, useNautilusSelector } from "../../state";
import type {
  DesignerNode,
  DesignerNodeHandle,
  EventDefinition,
  NodeDesignerDescr,
  TriggersComponent,
} from "../../types";
import { useDesignerNodeComponentProps } from "../../useNodeDesignerComponentProps";
import { useTriggerDesigns, useTriggersForNode } from "../TriggerDesignsContext";
import { TriggerSection } from "./TriggerSection";
import { useNotifyTriggerCaseAdd } from "./useNotifyTriggerCaseAdd";

type Props = {
  eventTypes?: readonly string[];
  node: DesignerNodeHandle;
  title?: string;
};

export const TriggersDesigner = ({ eventTypes: specifiedEventTypes, node, title }: Props) => {
  const fullNode = useNautilusSelector((state) => designerNodeOrNullSelector(state, { nodeId: node.id }));
  const { getTriggerEvents, getCustomComponent } = useTriggerDesigns();

  if (!fullNode) return null;

  const eventDefinitions = specifiedEventTypes
    ? getTriggerEvents(fullNode).filter((def) => specifiedEventTypes.includes(def.type))
    : getTriggerEvents(fullNode);
  const component = getCustomComponent(fullNode);

  if (component) {
    return <CustomTriggersView node={fullNode} eventDefinitions={eventDefinitions} component={component} />;
  }

  if (eventDefinitions.length === 0) {
    return null;
  }

  return <Standard id={fullNode.id} eventDefinitions={eventDefinitions} title={title} />;
};

export type TriggerWithDefinition = {
  trigger: TriggerDetails;
  definition: EventDefinition;
};

const resolveDefinitions = (
  triggers: TriggerDetails[],
  eventDefinitionsByType: Map<string, EventDefinition>,
): TriggerWithDefinition[] => {
  const results: TriggerWithDefinition[] = [];
  for (const trigger of triggers) {
    const eventType = trigger.cases[0]?.eventType;
    if (!eventType) continue;
    const definition = eventDefinitionsByType.get(eventType);
    if (!definition) continue;
    results.push({ trigger, definition });
  }
  return results;
};

type TriggersProps = { eventDefinitions: ReadonlyArray<EventDefinition>; id: number };

type StandardProps = TriggersProps & { title?: string };

const Triggers = ({ id, eventDefinitions }: TriggersProps) => {
  const triggers = useTriggersForNode(id);

  const eventDefinitionsByType = useMemo(
    () => new Map(eventDefinitions.map((def) => [def.type, def])),
    [eventDefinitions],
  );

  const triggersWithDefinitions = useMemo(
    () => resolveDefinitions(triggers, eventDefinitionsByType),
    [triggers, eventDefinitionsByType],
  );

  return triggersWithDefinitions.map((triggerWithDefinition) => (
    <TriggerSection key={triggerWithDefinition.trigger.id} nodeId={id} triggerWithDefinition={triggerWithDefinition} />
  ));
};

TriggersDesigner.Triggers = Triggers;

const Standard = ({ id, eventDefinitions, title = "Events" }: StandardProps) => {
  const formatMessage = useFormatMessage(formatStrings);
  const { addTrigger } = useTriggerDesigns();

  const handleAdd = (type: string, where?: TriggerConditionBuild) => addTrigger(id, type, where);

  const triggers = useTriggersForNode(id);

  const addableEventDefinitions = useMemo(() => {
    const triggerCountByEventType = new Map<string, number>();
    for (const trigger of triggers) {
      const eventType = trigger.cases[0]?.eventType;
      if (eventType) {
        triggerCountByEventType.set(eventType, (triggerCountByEventType.get(eventType) ?? 0) + 1);
      }
    }
    const isSingleTrigger = (def: EventDefinition) => def.singleTrigger || !def.whereConditionBuilder;
    return eventDefinitions.filter((def) => !isSingleTrigger(def) || !triggerCountByEventType.has(def.type));
  }, [eventDefinitions, triggers]);

  return (
    <>
      <PropertiesPanelSection
        empty
        title={title}
        data-test="add-events-container"
        controls={
          <Tooltip title={formatMessage("addEvent")} placement="top">
            <div>
              <ContextMenu
                IconComponent={<Icon icon="plus" />}
                data-test="add-event"
                disabled={addableEventDefinitions.length === 0}
              >
                {addableEventDefinitions.map((def) => (
                  <AddTriggerMenuItem key={def.type} onAdd={handleAdd} definition={def} nodeId={id} />
                ))}
              </ContextMenu>
            </div>
          </Tooltip>
        }
      />

      <Triggers id={id} eventDefinitions={eventDefinitions} />
    </>
  );
};

TriggersDesigner.Standard = Standard;

const AddTriggerMenuItem = ({
  definition,
  onAdd,
  nodeId,
}: {
  onAdd: (type: EventDefinition["type"], where?: TriggerConditionBuild) => void;
  definition: EventDefinition;
  nodeId: number;
}) => {
  const formatCustom = useDynamicFormatString();
  const notifyTriggerCaseAdd = useNotifyTriggerCaseAdd(nodeId);

  const handleAdd = () => {
    notifyTriggerCaseAdd(definition);
    const initialConfig = definition.whereConditionBuilder?.getInitialConfig?.();
    onAdd(definition.type, initialConfig ? { config: initialConfig } : undefined);
  };

  return (
    <MenuItem onClick={handleAdd} data-test={`add-event-${definition.type}`}>
      <MenuItemLabel>{formatCustom(definition.title)}</MenuItemLabel>
    </MenuItem>
  );
};

// to avoid conditional hooks
const CustomTriggersView: ComponentType<{
  node: DesignerNode;
  eventDefinitions: readonly EventDefinition[];
  component: TriggersComponent<NodeDesignerDescr>;
}> = ({ node, eventDefinitions, component: CustomTriggersComponent }) => {
  const nodeProps = useDesignerNodeComponentProps({ node });
  const { removeAllTriggers } = useTriggerDesigns();

  const handleRemoveAllTriggers = useCallback(() => removeAllTriggers(node.id), [removeAllTriggers, node.id]);

  // null while rendering a just-deleted node (before it unmounts)
  if (!nodeProps) {
    return null;
  }
  return (
    <CustomTriggersComponent
      {...nodeProps}
      eventDefinitions={eventDefinitions}
      onRemoveAllTriggers={handleRemoveAllTriggers}
    />
  );
};

const formatStrings = {
  addEvent: {
    en: "Add Event",
  },
} satisfies FormatMessages;
